import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { CivilState } from '@app/forms/subscription/definition/interface/subscription';
import { FormValidators } from '@app/providers/utils/form-validators';

import { Parameters } from '../../definition/interface/Pledge';
import { CompanyFieldType } from '../../definition/interface/enum';
import FormGroupArray from '../../services/FormGroupArray';
@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(
    entity: CivilState & { userHasEmail: boolean },
    option,
  ): FormGroup {
    const sessionConsultant = option?.sessionConsultant;
    const isCristalisOrganization = option?.isCristalisOrganization || false;
    const { hasSwisslifeProduct = false } = option || {};

    const otherFirstNamesValidator: ValidatorFn | null = hasSwisslifeProduct
      ? this.atLeastOneNonEmptyValue()
      : null;

    let socialRegimeValidation = this.socialSecurityNumberRequired(
      option?.pledgeParameters || {},
    );

    let parentsNamesValidation = this.parentsNamesRequired(
      option?.pledgeParameters || {},
    );

    let formBuilderGroup = this.formBuilder.group({
      ownerId: [entity?.ownerId],
      userTitle: [
        entity?.userTitle || 'M',
        isCristalisOrganization && sessionConsultant
          ? Validators.nullValidator
          : Validators.required,
      ],
      userLastName: [
        entity?.userLastName || null,
        isCristalisOrganization && sessionConsultant
          ? Validators.nullValidator
          : Validators.required,
      ],
      userFirstName: [
        entity?.userFirstName ? entity.userFirstName : null,
        isCristalisOrganization && sessionConsultant
          ? Validators.nullValidator
          : Validators.required,
      ],
      userBirthName: [entity?.userBirthName ? entity.userBirthName : null],

      userFatherLastName: [
        entity?.userFatherLastName || null,
        parentsNamesValidation ? Validators.required : Validators.nullValidator,
      ],
      userFatherFirstName: [
        entity?.userFatherFirstName || null,
        parentsNamesValidation ? Validators.required : Validators.nullValidator,
      ],

      userMotherLastName: [
        entity?.userMotherLastName || null,
        parentsNamesValidation ? Validators.required : Validators.nullValidator,
      ],
      userMotherFirstName: [
        entity?.userMotherFirstName || null,
        parentsNamesValidation ? Validators.required : Validators.nullValidator,
      ],

      userSocialSecurityNumber: [
        entity?.userSocialSecurityNumber || null,
        socialRegimeValidation
          ? [Validators.required, FormValidators.socialSecurityNumber]
          : Validators.nullValidator,
      ],
      userSocialRegime: [
        entity?.userSocialRegime || null,
        socialRegimeValidation ? Validators.required : Validators.nullValidator,
      ],
      userOtherFirstNames: this.formBuilder.array(
        [this.formControl()],
        otherFirstNamesValidator,
      ),
      userEmail: [
        entity?.userEmail || null,
        [
          isCristalisOrganization && sessionConsultant
            ? Validators.nullValidator
            : Validators.required,
          Validators.email,
        ],
      ],
      userHasEmail: [entity?.userHasEmail || null],
    });

    entity?.userOtherFirstNames?.length > 0 &&
      formBuilderGroup.setControl(
        'userOtherFirstNames',
        this.populateControl(entity, otherFirstNamesValidator),
      );

    return formBuilderGroup;
  }

  socialSecurityNumberRequired(parameters: Parameters) {
    return (
      this.getAdditionalQuestions(
        parameters,
        CompanyFieldType.SOCIAL_SECURITY_NUMBER,
      )?.required === 'MANDATORY' || false
    );
  }

  parentsNamesRequired(parameters: Parameters) {
    return (
      this.getAdditionalQuestions(parameters, CompanyFieldType.PARENTS_NAMES)
        ?.required === 'MANDATORY' || false
    );
  }

  getAdditionalQuestions(parameters: Parameters, type: any) {
    return parameters?.additionalQuestions?.find(q => q?.id === type);
  }

  generate(entity: CivilState[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
    });
  }

  formControl() {
    return this.formBuilder.control('');
  }

  atLeastOneNonEmptyValue(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const array = control as UntypedFormArray;
      const hasValue = array.controls.some(
        c => c.value != null && `${c.value}`.trim() !== '',
      );
      return hasValue ? null : { required: true };
    };
  }

  populateControl(data: CivilState, validator: ValidatorFn | null = null) {
    let formArray = this.formBuilder.array([this.formControl()], validator);
    if (data && data.userOtherFirstNames) {
      formArray.clear();
      data.userOtherFirstNames.forEach((item, index) => {
        formArray.push(this.formBuilder.control(item || ''));
      });
    }
    return formArray;
  }

  extract() {
    return this.form.value.owners;
  }
}
@Injectable({ providedIn: 'root' })
export class CivilStateValidation extends EaSubscriptionBuilder {
  onSubmit() {
    throw new Error('Method not implemented.');
  }

  isValid(entity) {
    return this.generate(entity, {})?.valid;
  }
}
